import { apis } from '@src/shared/api';
import { logger } from '@src/shared/includes/logger';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { isFunction, isUndefined, merge } from 'lodash';
import { useState } from 'react';

/**
 * Hook: useApiQuery
 *
 * Dynamic api request function based on query key to make on-demand api requests.
 *
 * @param apiKey
 * @param queryKey
 * @param options
 * @returns {*[]}
 */
export function useApiQuery(apiKey = 'Default', queryKey = '', options = {}) {
  const [enabled, setEnabled] = useState(false);
  const [queryArgs, setQueryArgs] = useState({});
  const { toast } = useToast();

  let queryFunction;

  if (!isUndefined(apis?.[apiKey]?.queries?.[queryKey])) {
    queryFunction = apis?.[apiKey]?.queries?.[queryKey];
  }

  const theQuery = merge(
    {},
    {
      enabled,
      excludedHttpCodes: [],
      queryFn: async (args) => {
        try {
          let response;

          if (isUndefined(queryFunction)) {
            throw new Error(`Request not found for api key: ${apiKey} and query key: ${queryKey}`);
          }

          if (isFunction(queryFunction)) {
            response = await queryFunction(args?.queryKey);
          }

          setEnabled(false);
          return response;
        } catch (errorObject) {
          setEnabled(false);

          const error = errorObject?.error || errorObject;
          const excludedHttpCodes = options?.excludedHttpCodes || [];

          if (!excludedHttpCodes?.includes(error?.statusCode)) {
            toast.show({
              autoClose: false,
              id: `useApiQuery-${apiKey}-${queryKey}`,
              message: error?.message || 'Unknown error',
              title: `API Error - ${queryKey}`,
              variant: 'error',
            });
          }

          logger.error('src/app/context/Api/hooks/useApiQuery/useApiQuery.jsx -> useApiQuery', false);
          throw errorObject;
        }
      },
      queryKey: [apiKey, queryKey, queryArgs],
    },
    options
  );

  const query = useQuery(theQuery);

  /**
   * requestFunction
   *
   * Sets a state value to define request args and enable the query to make a fetch request with those args.
   *
   * @param args
   * @returns {Promise<void>}
   */
  const requestFunction = (args) => {
    setQueryArgs(args);
    setEnabled(true);
  };

  return [requestFunction, query];
}
